import cn from 'classnames';
import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import { Input } from 'src/components/common/Input';
import { Typography } from 'src/components/common/Typography';
import { getSkinId } from 'src/helpers/utils';
import { forgotPasswordValidationScheme } from 'src/helpers/validationScheme';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { forgotPasswordRequest } from 'src/services/authorizationApi';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { ForgotPassword } from 'src/types/req-dto/authorization';
import { IForgotRes } from 'src/types/res-dto/authorization';
import { RootState } from 'src/types/store-types';
import _styles from './styles.module.scss';

interface IProps extends IForgotRes {
  close: () => void;
  setContent: (action: 'open' | 'close', content: 'login' | 'registration' | 'forgot' | null) => void;
}

type Form = {
  email?: string;
  username?: string;
  cpf?: string;
};

const ForgotPassContent: FC<IProps> = ({ close, username, email, cpf }) => {
  const { t }: Translation = useTranslation();
  const skinId = getSkinId();

  const { forgotContentText } = useSelector((state: RootState) => state.authModal);

  const _openDialog = useConnectedAction(openDialog);

  const [generalError, setGeneralError] = useState<string>('');

  const handleSubmitForgot = async (): Promise<void> => {
    const payload: ForgotPassword = {
      skinId,
    };

    if (values?.email) {
      payload.email = values.email.trim().toLowerCase();
    }

    if (values?.username) {
      payload.username = values.username.trim();
    }

    if (values?.cpf) {
      payload.cpf = values.cpf.trim();
    }
    const res = await forgotPasswordRequest(payload);

    if (!!res.success) {
      setGeneralError('');
      close();
      _openDialog({
        dialogType: EnumDialogsKeys.RESET_PASSWORD_SUCCESS,
        dialogProps: { email: res.email, dialogCase: 'forgotPassword' },
      });
    } else if (['document_not_found', 'unprocessable_entity'].includes(res.message?.key)) {
      setGeneralError(Boolean(cpf) ? 'incorrectCpf' : 'incorrectUsernameOrEmail');
    }
  };

  const formik = useFormik<Form>({
    validationSchema: forgotPasswordValidationScheme(t, Boolean(email), Boolean(username), Boolean(cpf)),
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      email: '',
      username: '',
      cpf: '',
    },
    onSubmit: async () => {
      await handleSubmitForgot();
    },
  });

  const { values, getFieldProps, handleSubmit, errors, touched } = formik;

  return (
    <>
      <p className={_styles.subText}>{forgotContentText ? t(forgotContentText) : t('forgot_pass_text')}</p>
      <p className={_styles.subText}>{t('forgot_pass_additional_text')}</p>
      <div className={_styles.forgot_password_container}>
        {Boolean(username) && (
          <Input
            {...getFieldProps('username')}
            value={values.username as string}
            label={'username'}
            error={(!!touched.username && !!errors.username && t(errors.username)) || ''}
          />
        )}
        {Boolean(email) && (
          <Input
            {...getFieldProps('email')}
            value={values.email as string}
            label={'email'}
            error={(!!touched.email && !!errors.email && t(errors.email)) || ''}
          />
        )}
        {Boolean(cpf) && (
          <Input
            {...getFieldProps('cpf')}
            value={values.cpf as string}
            label={'cpf'}
            error={(!!touched.cpf && !!errors.cpf && t(errors.cpf)) || ''}
          />
        )}
      </div>
      {generalError && (
        <Typography variant={'body3'} className={_styles.invalid_error} color="error">
          {t(generalError)}
        </Typography>
      )}
      <Button className={cn(_styles.recoverButton, 'success-filled-btn')} onClick={handleSubmit}>
        {t('button_recover')}
      </Button>
    </>
  );
};

export default ForgotPassContent;
